// Two-layer dataset upload validation (HuggingFace excluded - external source).
// Layer 1: extension must match the selected dataset type.
// Layer 2: content must parse as the claimed format.
//
// Notes:
// - The <input accept> attribute is only a file-picker hint, not real validation.
// - Excel files are binary, so we validate extension, file signature, and workbook parsing.

import * as XLSX from "xlsx";

export const allowedExtensions = {
  csv: [".csv"],
  text: [".txt"],
  excel: [".xlsx", ".xls"],
  json: [".json"],
};

export const getFileExtension = (filename) => {
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex === -1 ? "" : filename.slice(dotIndex).toLowerCase();
};

export const checkExtension = (filename, datasetType) => {
  const expected = allowedExtensions[datasetType];

  // Unlisted types (HuggingFace) have no local file to check, so they pass.
  if (!expected) {
    return { valid: true, expected: [] };
  }

  const extension = getFileExtension(filename);

  return {
    valid: expected.includes(extension),
    expected,
    extension,
  };
};

const MAX_REPORTED_ROWS = 5;

// Splits on commas exactly like the app's preview parser, so both agree on column
// counts. Trade-off: quoted fields containing commas are not handled.
export const validateCsvContent = (text) => {
  if (!text || text.trim().length === 0) {
    return { valid: false, details: ["File is empty."] };
  }

  const lines = text.trim().split(/\r?\n/);

  if (lines.length < 2) {
    return {
      valid: false,
      details: ["CSV needs a header row and at least one data row."],
    };
  }

  const expectedColumns = lines[0].split(",").length;

  if (expectedColumns < 2) {
    return {
      valid: false,
      details: [
        `Header row has only ${expectedColumns} column — values must be comma-separated.`,
      ],
    };
  }

  const raggedRows = [];

  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === "") continue;

    const columns = lines[i].split(",").length;

    if (columns !== expectedColumns) {
      raggedRows.push(
        `Row ${i + 1} has ${columns} columns, expected ${expectedColumns}.`
      );
    }
  }

  if (raggedRows.length > 0) {
    const details = raggedRows.slice(0, MAX_REPORTED_ROWS);

    if (raggedRows.length > MAX_REPORTED_ROWS) {
      details.push(
        `...and ${raggedRows.length - MAX_REPORTED_ROWS} more rows with mismatched columns.`
      );
    }

    return { valid: false, details };
  }

  return { valid: true };
};

export const validateJsonContent = (text) => {
  if (!text || text.trim().length === 0) {
    return { valid: false, details: ["File is empty."] };
  }

  try {
    JSON.parse(text);
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      details: [`Invalid JSON: ${error.message}`],
    };
  }
};

export const validateTextContent = (text) => {
  if (!text || text.trim().length === 0) {
    return { valid: false, details: ["File is empty."] };
  }

  if (text.includes("\u0000")) {
    return {
      valid: false,
      details: ["File contains binary data — not a plain text file."],
    };
  }

  return { valid: true };
};

export const validateExcelContent = (arrayBuffer, extension) => {
  const bytes = new Uint8Array(arrayBuffer);

  if (bytes.length === 0) {
    return { valid: false, details: ["File is empty."] };
  }

  // "PK" — .xlsx is a zip archive, so a renamed text file fails here.
  if (extension === ".xlsx") {
    const hasZipSignature = bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;

    if (!hasZipSignature) {
      return {
        valid: false,
        details: [
          "Not a valid .xlsx workbook — the file signature does not match the Excel format.",
        ],
      };
    }
  }

  // OLE compound-file header used by the legacy .xls format.
  if (extension === ".xls") {
    const oleSignature = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

    const hasOleSignature =
      bytes.length >= 8 && oleSignature.every((byte, index) => bytes[index] === byte);

    if (!hasOleSignature) {
      return {
        valid: false,
        details: [
          "Not a valid .xls workbook — the file signature does not match the Excel format.",
        ],
      };
    }
  }

  try {
    const workbook = XLSX.read(arrayBuffer, { type: "array" });

    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      return {
        valid: false,
        details: ["The Excel file does not contain any sheets."],
      };
    }

    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    if (!worksheet) {
      return {
        valid: false,
        details: ["The first Excel sheet could not be read."],
      };
    }

    const csvContent = XLSX.utils.sheet_to_csv(worksheet);

    if (!csvContent || !csvContent.trim()) {
      return {
        valid: false,
        details: ["The selected Excel sheet is empty."],
      };
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      details: [`Excel file could not be parsed: ${error.message}`],
    };
  }
};

export const validateContent = async (file, datasetType) => {
  switch (datasetType) {
    case "csv":
      return validateCsvContent(await file.text());

    case "json":
      return validateJsonContent(await file.text());

    case "text":
      return validateTextContent(await file.text());

    case "excel":
      return validateExcelContent(
        await file.arrayBuffer(),
        getFileExtension(file.name)
      );

    default:
      return { valid: true };
  }
};